/**
 * BA Search — native storefront search backed by the ba-search Cloudflare worker
 * (replaces the XCloud Search app; enabled via theme setting `ba_search_enabled`).
 *
 * Three modules:
 *  - Pre-search: renders the dropdown with XCloud-compatible markup
 *    (.cloud-search-results, dl/dt/dd, .cs-item …) so the existing custom CSS and
 *    the cloud-search-presearch-* enhancement scripts keep working unchanged.
 *  - Results page (/search): totals + sort controls, filter sidebar, product grid.
 *    Ranked handles come from the worker; cards are fetched server-rendered via the
 *    Section Rendering API (section_id=ba-search-grid) and reordered — theme-perfect
 *    cards with zero markup duplication.
 *  - In-collection: fills the sidebar mounts on collection pages; takes over the
 *    grid only once the visitor searches or filters (native grid otherwise).
 */
(() => {
  const cfg = window.BASearchConfig;
  if (!cfg || !cfg.url) return;
  if (window.__baSearchInit) return;
  window.__baSearchInit = true;

  const WORKER = cfg.url.replace(/\/+$/, '');
  const money = new Intl.NumberFormat('en-CA', { style: 'currency', currency: cfg.currency || 'CAD' });

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  function highlight(text, q) {
    const safe = esc(text);
    const tokens = String(q || '').trim().split(/\s+/).filter((t) => t.length >= 2);
    if (!tokens.length) return safe;
    const re = new RegExp(`(${tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
    return safe.replace(re, '<span class="highlight-hit">$1</span>');
  }

  async function fetchJSON(url) {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`ba-search ${res.status}`);
    return res.json();
  }

  // ────────────────────────────────────────────────────────────────
  // Pre-search dropdown
  // ────────────────────────────────────────────────────────────────

  let menuEl = null;
  let menuForInput = null;
  let suggestSeq = 0;
  let lastRenderedQuery = null;
  // ONE persistent preview panel: content swaps per hovered row, `top` simply
  // retargets and the CSS transition glides from wherever it currently is —
  // interruption-safe by construction (no per-row panels, no seeding, no rAF).
  let panelEl = null;
  let panelPlaced = false;
  const previews = new Map(); // row data-id -> { wide, html }
  // Enter submits the search form unless the user explicitly arrow-keyed onto a
  // row — the auto-activated first row (shown for its preview) must not count.
  let keyboardNav = false;

  function ensureMenu() {
    if (menuEl) return menuEl;
    menuEl = document.createElement('div');
    menuEl.id = 'cloud-search-results';
    menuEl.setAttribute('role', 'listbox');
    menuEl.setAttribute('tabindex', '-1');
    menuEl.setAttribute('hidden', '');
    menuEl.className = 'instant-search-menu cloud-search-results';
    menuEl.style.cssText = 'position:absolute;margin:0;';
    document.body.appendChild(menuEl);
    return menuEl;
  }

  // Remove the menu entirely on close, like XCloud did — details-modal.js and the
  // presearch enhancement scripts treat a fresh element as a fresh lifecycle.
  function hideMenu() {
    if (menuEl) {
      menuEl.remove();
      menuEl = null;
    }
    menuForInput = null;
    lastRenderedQuery = null;
    panelEl = null;
    panelPlaced = false;
    previews.clear();
    keyboardNav = false;
  }

  function menuVisible() {
    return !!menuEl && !menuEl.hasAttribute('hidden');
  }

  /**
   * Mobile drawer: the panel is full-screen height, so the keyboard covers its
   * bottom rather than truncating it. The scroll area therefore gets bottom
   * padding equal to whatever the keyboard (plus Safari's form accessory bar)
   * currently hides — every row can still be scrolled into the visible strip,
   * and the white surface never stops mid-screen.
   */
  function sizeDrawerList() {
    if (!menuEl) return;
    const list = menuEl.querySelector('.cloud-search-results__list');
    if (!list) return;
    const panelBottom = menuEl.getBoundingClientRect().bottom;
    const listTop = list.getBoundingClientRect().top;
    const vv = window.visualViewport;
    const hidden = vv ? Math.max(0, panelBottom - (vv.offsetTop + vv.height)) : 0;
    list.style.maxHeight = `${Math.max(160, Math.round(panelBottom - listTop))}px`;
    list.style.overflowY = 'auto';
    list.style.paddingBottom = `${Math.round(hidden) + 24}px`;
  }


  /**
   * details-modal.js owns ongoing repositioning of #cloud-search-results (it did
   * for XCloud too): it repositions on modal open/focus and watches the menu's
   * style attribute, correcting anything that doesn't match its expected
   * geometry — and each correction dispatches a synthetic window resize. Writing
   * any other geometry here makes the two managers correct each other forever
   * (the page-freeze bug). So write exactly what its observer expects
   * (inset top = input.bottom + 16, width 400px) and nothing else.
   */
  /**
   * Mode classes decide row geometry (thumb size, single-panel layout), so
   * they must be on the menu BEFORE rows are inserted — otherwise the first
   * layout pass sizes them under desktop rules and the corrected size animates
   * in (.list-image has `transition: all .2s`).
   */
  function applyMenuMode(input) {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    menuEl.classList.toggle('cloud-search-results--mobile', mobile);
    menuEl.classList.toggle('instant-search-menu--mobile-input', drawerOpen());
    menuEl.classList.toggle('cloud-search-results--single-panel', drawerOpen() || mobile);
    return mobile;
  }

  function positionMenu(input) {
    const mobile = applyMenuMode(input);
    const rect = input.getBoundingClientRect();
    // The dd detail panel opens to the menu's left by default; the 'left' class
    // flips it to the right — pick whichever side actually has room.
    const spaceRight = window.innerWidth - (rect.left + 400);
    menuEl.classList.toggle('left', !mobile && spaceRight >= 420);
    if (drawerOpen()) {
      // Full-screen fixed panel under the drawer chrome. The --mobile-input
      // class supplies padding-top (XCloud's mobile panel layout); position
      // must be inline because ensureMenu seeds position:absolute.
      menuEl.style.position = 'fixed';
      menuEl.style.inset = '0 auto auto 0px';
      menuEl.style.width = '100vw';
      menuEl.style.maxWidth = '100vw';
      // Always the full screen height — iOS Safari must never cut the panel
      // where the keyboard starts. dvh where supported (iOS 15.4+), vh
      // otherwise; neither shrinks for the keyboard. The panel itself doesn't
      // scroll (the list inside does), so the surface always reaches the
      // bottom of the screen.
      menuEl.style.height = '100vh';
      menuEl.style.height = '100dvh';
      menuEl.style.maxHeight = 'none';
      menuEl.style.overflow = 'hidden';
    } else if (mobile) {
      // Non-drawer mobile (e.g. the in-collection sidebar search): full
      // width, below the input (absolute in body → add page scroll).
      menuEl.style.position = 'absolute';
      const top = Math.round(rect.bottom + window.scrollY + 8);
      menuEl.style.inset = `${top}px auto auto 0px`;
      menuEl.style.width = '100vw';
      menuEl.style.maxWidth = '100vw';
    } else {
      // FIXED, not absolute: the coordinates come from getBoundingClientRect,
      // which is viewport-relative. As an absolutely-positioned element those
      // same numbers are read against the document, so opening the modal on a
      // scrolled page dropped the panel scrollY pixels above the input (the
      // mobile branch above compensates with window.scrollY; this one never
      // did). Fixed also keeps the panel glued to the input, which lives in a
      // fixed modal, instead of drifting if anything scrolls.
      const left = Math.round(rect.left);
      const top = Math.round(rect.bottom + 16);
      menuEl.style.position = 'fixed';
      menuEl.style.inset = `${top}px auto auto ${left}px`;
      menuEl.style.width = '400px';
      menuEl.style.maxWidth = `calc(100vw - ${left}px - 16px)`;
    }
    menuEl.style.zIndex = '10001';
  }

  /**
   * No-image rows keep a reserved square so titles stay aligned (what the
   * placeholders enhancement script did for XCloud). Its size is pinned inline
   * because the stylesheet that defines --presearch-list-thumb-width
   * (component-predictive-search.css) is deferred — and .list-image carries
   * `transition: all .2s`, so a late size change ANIMATES: the title starts
   * flush left and slides right over 200ms. Inline values + transition:none
   * make the square correct on the very first paint, always.
   */
  const RESERVED_STYLE = 'transition:none;flex:0 0 auto;'
    + 'width:var(--presearch-list-thumb-width,84px);'
    + 'min-width:var(--presearch-list-thumb-width,84px);'
    + 'min-height:var(--presearch-list-thumb-height,76px)';

  /* Brand collection images are white logo files (theme-wide convention:
     "logo" in the filename, same test the collection hero uses). The
     suggest panel is always LIGHT in both themes, so they must hard-invert
     or they render white-on-white. */
  function logoInvertClass(image) {
    return /logo/i.test(String(image || '')) ? ' cs-logo-invert' : '';
  }

  function rowImage(url, image, index) {
    return image
      ? `<a href="${esc(url)}" tabindex="-1" class="list-image${logoInvertClass(image)}">${rowThumb(image, index)}</a>`
      : `<a href="${esc(url)}" tabindex="-1" class="list-image list-image--reserved" style="${RESERVED_STYLE}"></a>`;
  }

  function priceBlock(p, i) {
    const onSale = p.compare_at > p.price;
    return onSale
      ? `<div class="cs-price-block"><div id="cloud-search-option-${i}-price" class="price cs-has-list-price">${money.format(p.price)}</div><div class="price cs-list-price">${money.format(p.compare_at)}</div></div>`
      : `<div class="cs-price-block"><div id="cloud-search-option-${i}-price" class="price ">${money.format(p.price)}</div></div>`;
  }

  function productDt(p, i, q) {
    const sku = esc(p.sku || '');
    return `
      <dt data-id="${esc(p.id)}" role="option" aria-selected="false" id="cloud-search-option-${i}"
          aria-labelledby="cloud-search-option-${i}-name cloud-search-option-${i}-price"
          data-index="${i}" class="cs-item">
        ${rowImage(p.url, p.image, i)}
        <div class="cs-item-details">
          ${priceBlock(p, i)}
          <a href="${esc(p.url)}" data-sku="${sku}" id="cloud-search-option-${i}-name" tabindex="-1"
             class="cs-product-link cs-item-title">${highlight(p.title, q)}</a>
        </div>
      </dt>`;
  }

  function productPreview(p, q) {
    const onSale = p.compare_at > p.price;
    const priceHtml = onSale
      ? `<div class="price cs-has-list-price">${money.format(p.price)}</div>
         <div class="price cs-list-price">${money.format(p.compare_at)}</div>`
      : `<div class="price ">${money.format(p.price)}</div>`;
    const desc = p.description
      ? `<div class="description">${highlight(truncate(p.description, 180), q)} …</div>`
      : '';
    const img = p.image
      ? `<div class="details-top-image"><a href="${esc(p.url)}" tabindex="-1" class="image"><img alt="" src="${esc(thumb(p.image))}"></a></div>`
      : '';
    return `
        <div class="details-top">
          <div class="details-top-text">
            <div class="name"><a href="${esc(p.url)}" data-sku="${esc(p.sku || '')}" tabindex="-1"
              class="cs-product-link cs-item-title">${highlight(p.title, q)}</a></div>
            ${priceHtml}
          </div>
          ${img}
        </div>
        <div class="clearing"></div>
        ${desc}
        <div class="see-details"><a href="${esc(p.url)}" tabindex="-1" class="cs-product-link">See details</a></div>`;
  }

  // ── Single-panel rows (mobile drawer) ──
  // XCloud's mobile layout: the whole row is the anchor, inner elements are
  // spans, descriptions render inline, and there are no dt/dd preview panels.
  // Markup mirrors prod verbatim so component-predictive-search.css's mobile
  // a.cs-item rules apply unchanged.

  function spImage(image, index) {
    return image
      ? `<span tabindex="-1" class="list-image">${rowThumb(image, index)}</span>`
      : `<span tabindex="-1" class="list-image list-image--reserved" style="${RESERVED_STYLE}"></span>`;
  }

  function productRowSP(p, i, q) {
    const desc = p.description
      ? `<div class="description">${highlight(truncate(p.description, 180), q)} …</div>`
      : '';
    return `
      <a href="${esc(p.url)}" data-id="${esc(p.id)}" role="option" aria-selected="false"
         id="cloud-search-option-${i}" aria-labelledby="cloud-search-option-${i}-name cloud-search-option-${i}-price"
         tabindex="-1" data-index="${i}" class="cs-item">
        ${spImage(p.image, i)}
        <div class="cs-item-details">
          ${priceBlock(p, i)}
          <span data-sku="${esc(p.sku || '')}" id="cloud-search-option-${i}-name" tabindex="-1"
            class="cs-product-link cs-item-title">${highlight(p.title, q)}</span>
          ${desc}
        </div>
      </a>`;
  }

  function linkRowSP(item, id, idx, q, kindClass) {
    // XCloud's mobile markup (captured from prod): category rows carry
    // `cs-item-description`; page/article rows carry `description` with the
    // products' trailing ellipsis. The theme's mobile CSS styles them
    // per-block, so the classes must match exactly.
    const isPage = kindClass === 'cs-page-link';
    const descClass = isPage ? 'description' : 'cs-item-description';
    const desc = item.description
      ? `<div class="${descClass}">${highlight(truncate(item.description, 180), q)}${isPage ? ' …' : ''}</div>`
      : '';
    return `
      <a href="${esc(item.url)}" data-id="${id}" role="option" aria-selected="false"
         id="cloud-search-option-${idx}" aria-labelledby="cloud-search-option-${idx}-name"
         tabindex="-1" data-index="${idx}" class="cs-item">
        ${spImage(item.image, idx)}
        <div class="cs-item-details">
          <span id="cloud-search-option-${idx}-name" tabindex="-1"
            class="${kindClass} cs-item-title">${highlight(item.title, q)}</span>
          ${desc}
        </div>
      </a>`;
  }

  function linkDt(item, id, idx, q) {
    return `
      <dt data-id="${id}" role="option" aria-selected="false" data-index="${idx}" class="cs-item">
        ${rowImage(item.url, item.image, idx)}
        <div class="cs-item-details">
          <a href="${esc(item.url)}" tabindex="-1" class="cs-item-title">${highlight(item.title, q)}</a>
        </div>
      </dt>`;
  }

  function linkPreview(item, q) {
    const img = item.image
      ? `<div class="details-top-image${logoInvertClass(item.image)}"><a href="${esc(item.url)}" tabindex="-1" class="image"><img alt="" src="${esc(thumb(item.image))}"></a></div>`
      : '';
    const desc = item.description
      ? `<div class="description">${highlight(truncate(item.description, 180), q)}</div>`
      : '';
    return `
        <div class="details-top">
          <div class="details-top-text">
            <div class="name"><a href="${esc(item.url)}" tabindex="-1" class="cs-item-title">${highlight(item.title, q)}</a></div>
          </div>
          ${img}
        </div>
        <div class="clearing"></div>
        ${desc}
        <div class="see-details"><a href="${esc(item.url)}" tabindex="-1">See details</a></div>`;
  }

  // Queries about the store itself get the address pinned above all results,
  // linking to the contact page. Word-boundary match keeps "headphones" etc.
  // from firing it.
  const STORE_INFO_RE = /\b(store|address|contact|location|direction)s?\b/i;
  function storeInfoHtml(q) {
    if (!STORE_INFO_RE.test(String(q || ''))) return '';
    return `
      <div role="group" aria-label="Store" class="results-block block-store-info">
        <a href="/pages/contact" class="cs-store-card">
          <span class="cs-store-card__name">Blackout Audio</span>
          <span class="cs-store-card__addr">1173 Dundas St East, Unit 136, Toronto, ON M4M 3P1</span>
          <span class="cs-store-card__link">Contact page: hours, phone &amp; directions</span>
        </a>
      </div>`;
  }

  function renderMenu(data, input) {
    const q = data.query;
    const blocks = [];
    let idx = 0;
    previews.clear();
    panelEl = null; // dies with the replaced markup
    panelPlaced = false;

    const storeCard = storeInfoHtml(q);
    if (storeCard) blocks.push(storeCard);

    // Drawer (or any mobile render) = XCloud's single-panel mode: whole-row
    // anchors, no previews.
    const sp = drawerOpen() || window.matchMedia('(max-width: 749px)').matches;
    const wrap = (rows) => (sp ? `<div>${rows}</div>` : `<dl role="none">${rows}</dl>`);
    if (data.products.length) {
      const rows = data.products.map((p) => {
        if (sp) return productRowSP(p, idx++, q);
        previews.set(String(p.id), { wide: true, html: productPreview(p, q) });
        return productDt(p, idx++, q);
      }).join('');
      blocks.push(`
        <div role="group" aria-label="Products" class="results-block block-products">
          <div role="heading" aria-level="2" class="header-right">Products</div>
          ${wrap(rows)}
        </div>`);
    }
    if (data.categories.length) {
      const rows = data.categories.map((c, i) => {
        if (sp) return linkRowSP(c, `c${i}`, idx++, q, 'cs-category-link');
        previews.set(`c${i}`, { wide: false, html: linkPreview(c, q) });
        return linkDt(c, `c${i}`, idx++, q);
      }).join('');
      blocks.push(`
        <div role="group" aria-label="Categories" class="results-block block-categories">
          <div role="heading" aria-level="2" class="header-right">Categories</div>
          ${wrap(rows)}
        </div>`);
    }
    if (data.pages.length) {
      const rows = data.pages.map((p, i) => {
        if (sp) return linkRowSP(p, `p${i}`, idx++, q, 'cs-page-link');
        previews.set(`p${i}`, { wide: false, html: linkPreview(p, q) });
        return linkDt(p, `p${i}`, idx++, q);
      }).join('');
      blocks.push(`
        <div role="group" aria-label="Pages" class="results-block block-pages">
          <div role="heading" aria-level="2" class="header-right">Pages</div>
          ${wrap(rows)}
        </div>`);
    }

    if (!blocks.length) { hideMenu(); return; }

    const summary = `${data.total} result${data.total === 1 ? '' : 's'}`;
    const seeMore = `
      <div class="see-more-results">
        <a href="${seeMoreUrl(q, input)}">See all ${data.total} results for “${esc(q)}”</a>
      </div>`;
    // Drawer mode: see-more scrolls with the list so the keyboard can't cover
    // it (the presearch-scroll helper's job under XCloud; it doesn't load with
    // BA Search). Desktop keeps it pinned after the list.
    ensureMenu();
    // Mode classes first: rows must be inserted already sized for this surface.
    applyMenuMode(input);
    menuEl.innerHTML = `
      <span role="status" class="cloud-search-visually-hidden">${esc(summary)}</span>
      <div class="cloud-search-results__list">${blocks.join('')}${drawerOpen() ? seeMore : ''}</div>
      ${drawerOpen() ? '' : seeMore}`;
    positionMenu(input);
    menuEl.removeAttribute('hidden');
    // Cap the list to the viewport (the scroll enhancement script's job under
    // XCloud) so long result sets scroll instead of overflowing the screen.
    const list = menuEl.querySelector('.cloud-search-results__list');
    if (drawerOpen()) {
      sizeDrawerList();
    } else if (list) {
      const vv = window.visualViewport;
      const viewportBottom = (vv ? vv.offsetTop + vv.height : window.innerHeight) - 16;
      const listTop = list.getBoundingClientRect().top;
      list.style.maxHeight = `${Math.max(120, Math.round(viewportBottom - listTop))}px`;
      list.style.overflowY = 'auto';
    }
    menuForInput = input;
    lastRenderedQuery = q;
    keyboardNav = false; // fresh results — Enter means "search", not "open row"
    const first = menuEl.querySelector('dt.cs-item');
    if (first) setActive(first);
  }

  function seeMoreUrl(q, input) {
    // The collection search box "sees all" within its collection.
    if (input?.hasAttribute('data-ba-collection-search')) {
      return `${window.location.pathname}?q=${encodeURIComponent(q)}`;
    }
    return `/search?q=${encodeURIComponent(q)}`;
  }

  function thumb(url, w = 300) {
    if (!url) return '';
    return url.includes('?') ? `${url}&width=${w}` : `${url}?width=${w}`;
  }

  // Rows past this point are off-screen on a phone; they still load
  // unconditionally, just at low priority behind the visible ones.
  const EAGER_ROWS = 4;

  /**
   * Row thumbnail. Three things make these appear faster:
   *  - Density-exact srcset instead of one oversampled file. Rows display at
   *    60–100px (100 is the widest: category thumbs), so 100/200/300w map to
   *    1x/2x/3x precisely. Nothing is softer than before at any density — 3x
   *    still gets 300w — but a 2x laptop now pulls ~14KB instead of ~26KB and
   *    a 1x screen ~5KB. Measured: no rendition width loads faster than
   *    another (Shopify resizes in ~250ms either way), so bytes are the only
   *    lever that actually moves.
   *  - eager + high fetch priority for the top rows: on a collection or results
   *    page the browser is already queueing dozens of card images, and the
   *    dropdown's thumbs used to wait behind them.
   *  - graduated priority by row. Firing every thumb at high priority made them
   *    share bandwidth equally, so the top results — which have the biggest
   *    images — consistently finished LAST (measured: row 0 at 1219ms while
   *    row 9 was done at 712ms). Lower rows now queue behind the visible ones.
   *
   * Deliberately NOT loading="lazy" below the fold: inside a fixed, internally
   * scrolled panel that would hang the images on intersection behavior we
   * can't verify on iOS, and a permanently blank thumbnail is a worse failure
   * than a slightly later one. Every row still loads unconditionally.
   */
  function rowThumb(url, index = 0) {
    const priority = index < EAGER_ROWS ? 'high' : 'low';
    const srcset = `${esc(thumb(url, 100))} 1x, ${esc(thumb(url, 200))} 2x, ${esc(thumb(url, 300))} 3x`;
    // src is the 2x file: browsers that ignore srcset still get a sharp thumb.
    return `<img alt="" src="${esc(thumb(url, 200))}" srcset="${srcset}"`
      + ` loading="eager" decoding="async" fetchpriority="${priority}">`;
  }

  function truncate(s, n) {
    s = String(s || '');
    return s.length > n ? s.slice(0, n).replace(/\s+\S*$/, '') : s;
  }

  function attachPresearch() {
    let timer = null;

    // Keep-warm heartbeat: ping on load and every ~25s while the tab is
    // visible. Requests reuse the browser's kept-alive connection, so this
    // holds warm the exact edge machine the visitor's searches will hit —
    // under low traffic, machines evict the worker and the next request pays
    // a 3-5s re-instantiation (the stall XCloud's always-on SaaS never had).
    // A warm ping is a ~1ms no-op server-side.
    const warm = () => {
      if (!document.hidden) fetch(`${WORKER}/api/warm`).catch(() => {});
    };
    warm();
    setInterval(warm, 25_000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) warm();
    });

    document.addEventListener('input', (e) => {
      const input = e.target;
      if (!input.matches?.('input.search__input, input[type="search"].search__input, input[data-ba-collection-search]')) return;
      // Opt-out for fields that borrow .search__input purely for its styling
      // and filter something already on the page (the Brands A-Z directory).
      // They are not product search and must not raise the suggest panel.
      if (input.hasAttribute('data-ba-no-suggest')) return;
      // The collection-filter search field is a filter on mobile (it lives in
      // the slide-in pane) — no suggest dropdown there, matching XCloud.
      if (
        input.hasAttribute('data-ba-collection-search')
        && window.matchMedia('(max-width: 749px)').matches
      ) return;
      // Drawer open but keystrokes arriving from an OUTSIDE field: iOS
      // sometimes rejects the programmatic focus() that should have moved the
      // cursor into the drawer, so typing lands in the page field hidden
      // behind it — results render but the drawer bar stays empty. Reroute
      // the text and the focus into the drawer.
      if (
        drawerOpen()
        && input.matches('input.search__input')
        && !input.closest('.cloud-search-mobile')
        && !input.hasAttribute('data-ba-collection-search')
      ) {
        drawerInput.value = input.value;
        drawerInput.focus();
        drawerInput.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
      // Mobile: typing in a page search field hands off to the full-screen
      // drawer, seeded with the field's text. Backstop for the focus handoff
      // below (covers programmatic focus paths that skip focusin).
      if (
        !drawerOpen()
        && window.matchMedia('(max-width: 749px)').matches
        && input.matches('input.search__input')
        && !input.closest('.cloud-search-mobile')
      ) {
        openMobileDrawer(input);
        return;
      }
      const q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 1) { hideMenu(); return; }
      timer = setTimeout(async () => {
        // Guard 1: never render for a detached or invisible input (e.g. the
        // paste-fix script re-fires `input` after the modal already closed).
        if (!document.contains(input) || input.getBoundingClientRect().width === 0) {
          hideMenu();
          return;
        }
        // Guard 2: idempotency — the same query is already on screen. This also
        // starves any script that echoes `input` events back at us.
        if (menuVisible() && menuForInput === input && lastRenderedQuery === q) return;
        const seq = ++suggestSeq;
        try {
          const scoped = input.hasAttribute('data-ba-collection-search') && cfg.collection
            ? `&collection=${encodeURIComponent(cfg.collection)}`
            : '';
          const data = await fetchJSON(`${WORKER}/api/suggest?q=${encodeURIComponent(q)}${scoped}`);
          if (seq !== suggestSeq) return; // stale response
          if ((input.value || '').trim() !== q) return;
          renderMenu(data, input);
        } catch { hideMenu(); }
      }, 180);
    });

    // Capture phase: details-modal stopPropagation()s its summary/overlay/
    // close-button clicks, so a bubble-phase listener never hears them — the
    // dropdown then lingered through the modal's 300ms close animation, and
    // stayed overlaid when the header search opened over the inline bar's menu.
    document.addEventListener('click', (e) => {
      if (
        menuVisible() && !menuEl.contains(e.target) && e.target !== menuForInput
        && !e.target.closest?.('.cloud-search-mobile')
      ) {
        hideMenu();
        // One click off the search backs out of everything: if the dropdown
        // was dismissed by a click outside the search UI itself (input/field/
        // form), close the header modal with it — not one layer per click.
        // (The theme's own handler only closes on exact .modal-overlay hits,
        // leaving strip/header clicks to strand the empty modal.)
        if (!e.target.closest?.('.search-modal__form, .field')) {
          const headerModal = document.querySelector('details-modal.header__search');
          if (headerModal?.querySelector('details[open]')) headerModal.close?.(false);
        }
      }
    }, true);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        hideMenu();
        closeMobileDrawer();
        closeFilterPane();
      }
      if (!menuVisible() || !menuForInput) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        keyboardNav = true;
        moveActive(e.key === 'ArrowDown' ? 1 : -1);
      } else if (e.key === 'Enter' && document.activeElement === menuForInput && keyboardNav) {
        // Desktop rows carry the link on the title anchor; single-panel rows
        // are the anchor themselves.
        const active = menuEl.querySelector('.cs-item.active');
        const url = active?.querySelector('a.cs-item-title')?.href || (active?.href ?? null);
        if (url) { e.preventDefault(); window.location.assign(url); }
      }
    });
    // Enter in any search field submits its form explicitly. The browser's
    // implicit submission is not reliable across our surfaces (the drawer's
    // form has no submit button, which disables it outright in some engines),
    // so one explicit path guarantees Enter always searches. Registered after
    // the menu-keydown handler above: when arrow-key navigation claims Enter
    // (preventDefault + row navigation), this sees defaultPrevented and yields.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || e.defaultPrevented) return;
      const input = e.target;
      if (!input.matches?.('input.search__input')) return;
      if (input.hasAttribute('data-ba-collection-search')) return;
      if (input.hasAttribute('data-ba-no-suggest')) return;
      const form = input.form;
      if (!form || typeof form.requestSubmit !== 'function') return;
      e.preventDefault();
      form.requestSubmit();
    });

    // Mobile: TAPPING a page search field opens the drawer immediately,
    // seeded with whatever the field holds (the current query on the results
    // page). Waiting for the first keystroke left the visitor typing into the
    // page input with the drawer nowhere in sight.
    document.addEventListener('focusin', (e) => {
      const input = e.target;
      if (!input.matches?.('input.search__input')) return;
      if (input.hasAttribute('data-ba-collection-search')) return;
      if (input.hasAttribute('data-ba-no-suggest')) return;
      if (input.closest('.cloud-search-mobile')) return;
      if (drawerOpen()) return;
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      openMobileDrawer(input);
    });

    // Keyboard show/hide (and iOS's visual-viewport scrolling) changes how much
    // of the drawer panel is covered — re-pad the scroll area so the last rows
    // stay reachable. Safe to listen to: visualViewport events are separate
    // from the synthetic window resizes details-modal.js fires, and that
    // repositioning is bypassed entirely while BA Search owns the menu.
    if (window.visualViewport) {
      const repad = () => { if (drawerOpen()) sizeDrawerList(); };
      window.visualViewport.addEventListener('resize', repad);
      window.visualViewport.addEventListener('scroll', repad);
    }

    // No window resize handler here on purpose: details-modal.js repositions
    // the menu itself and broadcasts synthetic resize events while doing so —
    // listening to them from here re-creates the reposition loop.

    // The header search lives in a <details> modal — close the dropdown with
    // it, and also close any dropdown anchored ELSEWHERE (e.g. the inline
    // results-page bar) the moment a search modal opens over it.
    document.addEventListener('toggle', (e) => {
      if (e.target.tagName !== 'DETAILS') return;
      if (!e.target.open) {
        if (menuForInput && e.target.contains(menuForInput)) hideMenu();
      } else if (menuVisible() && e.target.closest('details-modal') && !e.target.contains(menuForInput)) {
        hideMenu();
      }
    }, true);
    document.addEventListener('submit', (e) => {
      if (!e.target.matches?.('form.search')) return;
      const q = e.target.querySelector('input[name="q"]')?.value.trim();
      // Empty box: Enter / the magnifier is a no-op — no /search navigation,
      // no reload.
      if (!q) {
        e.preventDefault();
        return;
      }
      hideMenu();
      // Already on the search page: render the new query in place instead of a
      // full /search page load (Shopify rate-limits those under rapid use).
      if (cfg.template === 'search' && spaRender) {
        e.preventDefault();
        e.target.closest('details-modal')?.close?.(false);
        closeMobileDrawer();
        navigate({ q, page: 1, sort: 'relevance', priceMin: '', priceMax: '', types: [], vendors: [] }, '/search');
      }
    }, true);

    // Hover mirrors keyboard focus, like XCloud.
    document.addEventListener('mouseover', (e) => {
      const dt = e.target.closest?.('#cloud-search-results .cs-item');
      if (dt) setActive(dt);
    });

    // Whole-row click navigation (the rows enhancement script's job under
    // XCloud). Single-panel rows are anchors themselves and navigate natively.
    document.addEventListener('click', (e) => {
      const dt = e.target.closest?.('#cloud-search-results .cs-item');
      if (!dt || e.target.closest('a[href]')) return;
      const url = dt.querySelector('a.cs-product-link[href], a.cs-item-title[href], a.list-image[href]')?.href;
      if (url) window.location.assign(url);
    });
  }

  function moveActive(delta) {
    const items = [...menuEl.querySelectorAll('.cs-item')];
    if (!items.length) return;
    const current = menuEl.querySelector('.cs-item.active');
    const i = Math.max(0, Math.min(items.length - 1, items.indexOf(current) + delta));
    setActive(items[i]);
  }

  function ensurePanel() {
    if (panelEl && menuEl.contains(panelEl)) return panelEl;
    panelEl = document.createElement('dd');
    panelEl.className = 'active'; // base CSS: .instant-search-menu dd.active
    panelEl.innerHTML = '<div class="ba-preview-content"></div><div class="arrow"></div>';
    menuEl.appendChild(panelEl);
    panelPlaced = false;
    return panelEl;
  }

  function setActive(dt) {
    for (const el of menuEl.querySelectorAll('.cs-item.active')) el.classList.remove('active');
    dt.classList.add('active');
    // Single-panel rows (drawer/mobile) have no preview panels.
    if (menuEl.classList.contains('cloud-search-results--single-panel')) return;
    const id = dt.getAttribute('data-id');
    const preview = previews.get(id);
    const panel = ensurePanel();
    if (!preview) {
      panel.setAttribute('hidden', '');
      return;
    }
    panel.removeAttribute('hidden');
    if (panel.dataset.for !== id) {
      panel.dataset.for = id;
      panel.querySelector('.ba-preview-content').innerHTML = preview.html;
    }

    // Horizontal: side comes from the menu's `left` class, width from content
    // type (product previews are the wide 390px variant, like XCloud's
    // .block-products dd).
    const width = preview.wide ? 390 : 320;
    panel.style.width = `${width}px`;
    if (menuEl.classList.contains('left')) {
      panel.style.left = 'auto';
      panel.style.right = `-${width}px`;
    } else {
      panel.style.right = 'auto';
      panel.style.left = `-${width}px`;
    }

    // Vertical: top-align with the hovered row, clamped inside the menu. The
    // panel is ONE persistent element, so simply writing the new top lets the
    // CSS transition glide from wherever it currently is — including mid-flight
    // retargets. Only its very first placement is committed without animation.
    const menuRect = menuEl.getBoundingClientRect();
    const dtRect = dt.getBoundingClientRect();
    const rowTop = Math.max(0, Math.round(dtRect.top - menuRect.top));
    const panelH = panel.getBoundingClientRect().height;
    const clampedTop = Math.min(rowTop, Math.max(0, Math.round(menuRect.height - panelH)));
    const arrow = panel.querySelector('.arrow');
    const arrowTop = Math.min(
      Math.max(8, Math.round(rowTop - clampedTop + dtRect.height / 2 - 7)),
      Math.max(8, panelH - 22)
    );
    if (!panelPlaced) {
      panelPlaced = true;
      panel.style.transition = 'none';
      panel.style.top = `${clampedTop}px`;
      void panel.offsetHeight; // commit the initial position without animating
      panel.style.transition = '';
    } else {
      panel.style.top = `${clampedTop}px`;
    }
    if (arrow) arrow.style.top = `${arrowTop}px`;
  }

  // ────────────────────────────────────────────────────────────────
  // Mobile search drawer
  // ────────────────────────────────────────────────────────────────
  // Recreates XCloud's full-screen mobile takeover (its chunk-235 module):
  // scroll-locked page, blurred backdrop, fixed top chrome with its own input
  // and a Cancel link, results as a fixed full-width panel underneath. Reuses
  // the XCloud class vocabulary on purpose — base.css theme overrides and the
  // cloud-search-mobile-scroll-fix / presearch-scroll helpers key off it.

  let drawerEl = null;
  let drawerBackdropEl = null;
  let drawerInput = null;
  let drawerSourceInput = null; // the (hidden) theme input the drawer stands in for

  function drawerOpen() {
    return !!drawerEl;
  }

  function openMobileDrawer(fromInput) {
    // If drawerEl survives as a variable but has left the document - anything
    // that clears body children, a restore that dropped the node, a close
    // that did not run - this early return would focus a DETACHED input and
    // show nothing, on every tap, for the life of the page. Re-check that it
    // is actually connected and rebuild if not, so the drawer cannot get
    // stuck permanently shut.
    if (drawerEl && !drawerEl.isConnected) {
      drawerEl = drawerBackdropEl = drawerInput = null;
      document.documentElement.classList.remove('cloud-search-mobile-open');
    }
    if (drawerEl) {
      drawerInput?.focus();
      return;
    }
    drawerSourceInput = fromInput || null;

    drawerBackdropEl = document.createElement('div');
    drawerBackdropEl.className = 'cloud-search-mobile-backdrop';
    drawerBackdropEl.addEventListener('click', closeMobileDrawer);

    drawerEl = document.createElement('div');
    drawerEl.className = 'cloud-search-mobile';
    drawerEl.innerHTML = `
      <div class="cloud-search-mobile__input">
        <form action="/search" method="get" role="search" class="search">
          <input type="search" name="q" class="search__input" placeholder="Search"
            aria-label="Search" role="combobox" aria-expanded="false" aria-haspopup="listbox"
            aria-autocomplete="list" autocorrect="off" autocomplete="off" autocapitalize="off"
            spellcheck="false">
        </form>
      </div>
      <a href="" role="button" class="cloud-search-mobile__cancel">Cancel</a>`;
    drawerEl.querySelector('.cloud-search-mobile__cancel').addEventListener('click', (e) => {
      e.preventDefault();
      closeMobileDrawer();
    });

    document.body.appendChild(drawerBackdropEl);
    document.body.appendChild(drawerEl);
    document.documentElement.classList.add('cloud-search-mobile-open');

    drawerInput = drawerEl.querySelector('input[type=search]');
    const seed = (fromInput?.value || '').trim();
    if (seed) {
      drawerInput.value = seed;
      drawerInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    // Focus inside the tap's gesture window so mobile keyboards open.
    drawerInput.focus();
  }

  function closeMobileDrawer() {
    if (!drawerEl) return;
    hideMenu();
    drawerEl.remove();
    drawerBackdropEl.remove();
    drawerEl = drawerBackdropEl = drawerInput = null;
    document.documentElement.classList.remove('cloud-search-mobile-open');
    // The source field keeps its text (XCloud's cancel leaves the original
    // input untouched — e.g. the results-page field still shows its query).
    drawerSourceInput = null;
  }

  // details-modal.js hands the mobile search-icon tap to this instead of
  // opening the theme modal (as it handed off to XCloud before).
  window.BASearchMobile = { open: openMobileDrawer, close: closeMobileDrawer, isOpen: drawerOpen };

  // Growing past the mobile breakpoint with the drawer open: hand back to the
  // desktop UI (XCloud closed its mobile input on the same transition). Keep
  // the MediaQueryList referenced — unreferenced ones can be GC'd with their
  // listeners — and back it up with a resize check. (The no-resize-handler rule
  // above is about repositioning; closing on a breakpoint cross can't loop.)
  const desktopMQ = window.matchMedia('(min-width: 750px)');
  desktopMQ.addEventListener('change', (e) => {
    if (e.matches) closeMobileDrawer();
  });
  window.addEventListener('resize', () => {
    if (drawerOpen() && desktopMQ.matches) closeMobileDrawer();
  });

  // Back/forward restore (Safari keeps whole pages in the bfcache): the DOM
  // and this module's variables come back exactly as they were, so a drawer
  // left open when the visitor navigated away is restored open, and the
  // html class with it. Same pattern the theme already uses in
  // marquee-visibility.js and shipping-protection.js.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    closeMobileDrawer();
    document.documentElement.classList.remove('cloud-search-mobile-open');
  });

  // ────────────────────────────────────────────────────────────────
  // Shared: state <-> URL, sidebar, controls
  // ────────────────────────────────────────────────────────────────

  const SORT_OPTIONS = [
    ['relevance', 'Relevance'],
    ['created-descending', 'Newest Arrivals'],
    ['price-ascending', 'Lowest price'],
    ['price-descending', 'Highest price'],
    ['title-ascending', 'Alphabetically, A-Z'],
    ['title-descending', 'Alphabetically, Z-A'],
  ];

  function readStateFrom(p) {
    return {
      q: p.get('q') || '',
      page: parseInt(p.get('page') || '1', 10) || 1,
      sort: p.get('sort') || 'relevance',
      priceMin: p.get('price_min') || '',
      priceMax: p.get('price_max') || '',
      types: (p.get('types') || '').split('||').filter(Boolean),
      vendors: (p.get('vendors') || '').split('||').filter(Boolean),
    };
  }

  // Tag-filtered collection views (/collections/<handle>/<tag>) carry the
  // tag in the PATH, not the query. The worker scopes engaged queries by it
  // (Deal Zone tabs); un-engaged views keep the native tag-filtered grid.
  function collectionTagFromPath() {
    if (!cfg.collection || cfg.template !== 'collection') return '';
    const m = window.location.pathname.match(/\/collections\/[^/]+\/([^/?#]+)\/?$/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function readState() {
    const s = readStateFrom(new URLSearchParams(window.location.search));
    s.tag = collectionTagFromPath();
    return s;
  }

  // Match the collection pages' 48-per-page (XCloud was stuck at 20 because its
  // Liquid proxy rendered through all_products, hard-capped by Shopify).
  const PER_PAGE = 48;

  function stateToParams(state, { forApi = false } = {}) {
    const p = new URLSearchParams();
    if (state.q) p.set('q', state.q);
    if (state.page > 1) p.set('page', String(state.page));
    if (state.sort && state.sort !== 'relevance') p.set('sort', state.sort);
    if (state.priceMin !== '') p.set('price_min', state.priceMin);
    if (state.priceMax !== '') p.set('price_max', state.priceMax);
    if (state.types.length) p.set('types', state.types.join('||'));
    if (state.vendors.length) p.set('vendors', state.vendors.join('||'));
    if (forApi && state.collection) p.set('collection', state.collection);
    if (forApi && state.tag) p.set('tag', state.tag);
    if (forApi && !state.q) p.set('q', '*');
    if (forApi && state.sort === 'relevance') p.set('sort', 'relevance');
    if (forApi) p.set('per_page', String(PER_PAGE));
    return p;
  }

  // Set per page type (search / collection). When present and we're staying on
  // the same path, filter/sort/pagination changes render in place via
  // pushState — full /search page loads are what trips Shopify's storefront
  // rate limiter under rapid interaction (and they're slower anyway).
  let spaRender = null;

  // Visible search fields must always show the query actually rendered. After
  // a drawer/modal submit renders in place, the inline results-page bar still
  // holds only the FIRST keystroke (typed before the mobile drawer handoff) —
  // "M" on screen while "monitors" results render. Full page loads don't need
  // this (Liquid refills from search.terms). Collection-search fields are
  // excluded: their query is independent of the page-level one.
  function syncSearchInputs(q) {
    for (const el of document.querySelectorAll('input.search__input')) {
      if (el.hasAttribute('data-ba-collection-search')) continue;
      if (el.hasAttribute('data-ba-no-suggest')) continue;
      if (el.value !== q) el.value = q;
    }
  }

  function navigate(state, basePath, { scroll = true } = {}) {
    closeFilterPane();
    const url = `${basePath}?${stateToParams(state)}`;
    if (spaRender && window.location.pathname === basePath) {
      history.pushState({ baSearch: true }, '', url);
      if (basePath === '/search') syncSearchInputs(state.q || '');
      spaRender(state, { scroll });
    } else {
      window.location.assign(url);
    }
  }

  function filterGroup(title, values, selected, cssName) {
    const rows = values.map((v) => `
      <label data-filter-value="${esc(v.value)}" class="cloud-search-filter-value">
        <span class="cloud-search-filter-value__checkbox"><input type="checkbox" data-ba-facet="${cssName}"
          value="${esc(v.value)}"${selected.includes(v.value) ? ' checked' : ''}></span>
        <span class="cloud-search-filter-value__name">${esc(v.value)}</span>&nbsp;<span
          class="cloud-search-filter-value__count">(${v.count})</span>
      </label>`).join('');
    return `
      <div data-filter-name="${esc(title)}" class="cloud-search-filter">
        <div class="cloud-search-filter__name"><span class="cloud-search-filter__collapse"></span><span>${esc(title)}</span></div>
        <div class="cloud-search-text-field cloud-search-text-field--value-search">
          <input type="search" placeholder="Search ${esc(title)}" aria-label="Search ${esc(title)}" autocomplete="off"
            class="cloud-search-text-field__input cloud-search-text-field__input--value-search" data-ba-value-search>
        </div>
        <div class="cloud-search-filter__values">${rows}</div>
      </div>`;
  }

  // Applied-filter chips + "Clear all filters", XCloud markup/classes.
  function appliedFiltersHtml(state, { collectionSearch = false } = {}) {
    const chips = [];
    const chip = (label, values, key) => `
      <div class="cloud-search-applied-filter cloud-search-applied-filter--transparent">
        <span class="cloud-search-applied-filter__text">${esc(label)}:&nbsp;</span>
        <span class="cloud-search-applied-filter__values">${esc(values)}</span>
        <button type="button" class="cloud-search-applied-filter__clear" data-ba-remove-filter="${key}"
          aria-label="Remove ${esc(label)} filter">×</button>
      </div>`;
    if (collectionSearch && state.q) chips.push(chip('Search', state.q, 'q'));
    if (state.types.length) chips.push(chip('Categories', state.types.join(', '), 'types'));
    if (state.vendors.length) chips.push(chip('Brands', state.vendors.join(', '), 'vendors'));
    if (state.priceMin !== '' || state.priceMax !== '') {
      const min = state.priceMin !== '' ? `$${state.priceMin}` : '';
      const max = state.priceMax !== '' ? `$${state.priceMax}` : '';
      chips.push(chip('Price', `${min} – ${max}`.trim(), 'price'));
    }
    if (!chips.length) return '';
    return `
      <div class="ba-search-applied">
        ${chips.join('')}
        <div class="cloud-search-clear-filters" data-ba-clear-filters role="button" tabindex="0">Clear all filters</div>
      </div>`;
  }

  // The filter groups themselves — shared by the desktop sidebar and the
  // mobile slide-in pane, which differ only in chrome around this column.
  function filterColumnHtml(data, state, { collectionSearch = false } = {}) {
    const price = data.facets.price;
    const searchField = collectionSearch ? `
      <div data-filter-name="Search" class="cloud-search-filter">
        <div class="cloud-search-filter__name"><span>Search this collection</span></div>
        <div class="cloud-search-text-field">
          <input type="search" placeholder="Search" aria-label="Search this collection" autocomplete="off"
            class="cloud-search-text-field__input" data-ba-collection-search value="${esc(state.q)}">
        </div>
      </div>` : '';
    // A degenerate range (min === max, e.g. $0–$0 on a set with no priced
    // spread) leaves nothing to filter and renders a broken-looking slider —
    // skip the whole group. An applied price filter can still be cleared via
    // its chip in the applied-filters row.
    const priceGroup = Number(price.max) > Number(price.min) ? `
          <div data-filter-name="Price" class="cloud-search-filter">
            <div class="cloud-search-filter__name"><span class="cloud-search-filter__collapse"></span><span>Price</span></div>
            <div class="cloud-search-range-inputs">
              <div class="cloud-search-text-field cloud-search-num-field">
                <span class="cloud-search-num-field__currency">$</span>
                <input type="text" aria-label="Min price" inputmode="decimal" placeholder="${price.min}"
                  value="${esc(state.priceMin)}" data-ba-price="min"
                  class="cloud-search-text-field__input cloud-search-num-field__input">
              </div>
              <span> – </span>
              <div class="cloud-search-text-field cloud-search-num-field">
                <span class="cloud-search-num-field__currency">$</span>
                <input type="text" aria-label="Max price" inputmode="decimal" placeholder="${price.max}"
                  value="${esc(state.priceMax)}" data-ba-price="max"
                  class="cloud-search-text-field__input cloud-search-num-field__input">
              </div>
            </div>
            <div aria-hidden="true" class="cloud-search-range-slider" data-ba-price-slider
                 data-min="${price.min}" data-max="${price.max}">
              <div class="cloud-search-range-slider__bar"></div>
              <span class="cloud-search-range-slider__min">$${Number(price.min).toLocaleString('en-CA')}</span>
              <span class="cloud-search-range-slider__max">$${Number(price.max).toLocaleString('en-CA')}</span>
              <div class="cloud-search-range-slider__handle" data-ba-handle="min"></div>
              <div class="cloud-search-range-slider__handle" data-ba-handle="max"></div>
            </div>
          </div>` : '';
    return `
          ${searchField}
          ${priceGroup}
          ${filterGroup('Categories', data.facets.categories, state.types, 'types')}
          ${filterGroup('Brands', data.facets.brands, state.vendors, 'vendors')}`;
  }

  function sidebarHtml(data, state, { collectionSearch = false } = {}) {
    return `
      <div class="cloud-search-filters-sidebar">
        <div class="cloud-search-filters-sidebar__heading">Filters</div>
        ${appliedFiltersHtml(state, { collectionSearch })}
        <div class="cloud-search-filters-sidebar__filters cloud-search-filters-column">
          ${filterColumnHtml(data, state, { collectionSearch })}
        </div>
      </div>`;
  }

  // ── Filter pane (XCloud's slide-in drawer, opened by the "Filter" link) ──
  // Fixed full-height panel sliding in from the left over a fading backdrop;
  // chrome + animation classes match XCloud so the extracted
  // .cloud-search-filters-pane CSS (and theme overrides) style it as on prod.

  let filterPaneEl = null;
  let filterPaneBackdropEl = null;

  function openFilterPane(data, state, basePath, opts) {
    if (filterPaneEl || !data) return;

    filterPaneBackdropEl = document.createElement('div');
    filterPaneBackdropEl.className = 'cloud-search-filters-pane-backdrop cloud-search-filters-pane-backdrop--enter';
    filterPaneBackdropEl.addEventListener('click', closeFilterPane);

    filterPaneEl = document.createElement('div');
    filterPaneEl.className = 'cloud-search-filters-pane cloud-search-filters-pane--enter';
    filterPaneEl.innerHTML = `
      <div class="cloud-search-filters-pane__close" role="button" tabindex="0" aria-label="Close filters">
        <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M9.06 8l6.47-6.47L14.47.47 8 6.94 1.53.47.47 1.53 6.94 8 .47 14.47l1.06 1.06L8 9.06l6.47 6.47 1.06-1.06z"/></svg>
      </div>
      <div class="cloud-search-filters-pane__heading">Filters</div>
      ${appliedFiltersHtml(state, opts)}
      <div class="cloud-search-filters-column">
        ${filterColumnHtml(data, state, opts)}
      </div>`;
    filterPaneEl.querySelector('.cloud-search-filters-pane__close').addEventListener('click', closeFilterPane);

    document.body.appendChild(filterPaneBackdropEl);
    document.body.appendChild(filterPaneEl);
    document.body.style.overflow = 'hidden';
    bindSidebarEvents([filterPaneEl], state, basePath, opts);

    // Two frames so the --enter geometry commits before the transition runs;
    // timer fallback because rAF can be throttled (backgrounded tabs).
    const reveal = () => {
      filterPaneEl?.classList.remove('cloud-search-filters-pane--enter');
      filterPaneBackdropEl?.classList.remove('cloud-search-filters-pane-backdrop--enter');
    };
    requestAnimationFrame(() => requestAnimationFrame(reveal));
    setTimeout(reveal, 100);
  }

  function closeFilterPane() {
    if (!filterPaneEl) return;
    const pane = filterPaneEl;
    const backdrop = filterPaneBackdropEl;
    filterPaneEl = filterPaneBackdropEl = null;
    document.body.style.overflow = '';
    pane.classList.add('cloud-search-filters-pane--enter');
    backdrop.classList.add('cloud-search-filters-pane-backdrop--enter');
    setTimeout(() => { pane.remove(); backdrop.remove(); }, 350);
  }

  // Custom listbox instead of <select>: browsers render the native option
  // popup with unstylable legacy chrome once the closed control is themed.
  function sortDropdownHtml(state) {
    const currentSortName = (SORT_OPTIONS.find(([v]) => v === state.sort) || SORT_OPTIONS[0])[1];
    const sortOptions = SORT_OPTIONS.map(([v, name]) =>
      `<li><button type="button" class="ba-sort__option" role="option" data-ba-sort-option="${v}"
        aria-selected="${v === state.sort ? 'true' : 'false'}">${name}</button></li>`).join('');
    return `
      <div class="ba-sort" data-ba-sort-dd>
        <button type="button" class="ba-sort__btn" data-ba-sort-btn aria-haspopup="listbox" aria-expanded="false">
          <span>${currentSortName}</span>
          <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M 1.5 1 L 5 4.5 L 8.5 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
        </button>
        <ul class="ba-sort__list" data-ba-sort-list role="listbox" hidden>${sortOptions}</ul>
      </div>`;
  }

  function controlsHtml(data, state, { collectionPage = false, collectionSearch = false } = {}) {
    const first = data.total === 0 ? 0 : (data.page - 1) * data.per_page + 1;
    const last = Math.min(data.total, data.page * data.per_page);
    const label = state.q ? ` for "${esc(state.q)}"` : '';
    const sortDropdown = sortDropdownHtml(state);
    // --collection-page rows carry the tablet-band sort (750-990px); above that
    // the desktop toolbar mount takes over and CSS hides this row.
    const pageClass = collectionPage
      ? 'cloud-search-page-controls--collection-page'
      : 'cloud-search-page-controls--search-page';
    const sortBlock = `
          <div class="cloud-search-page-controls__sort">
            <span class="cloud-search-sort-label">Sort by:</span>
            ${sortDropdown}
          </div>`;
    // Collection rows put sort beside the totals on the left; the search page
    // keeps it grouped with the Filter link.
    return `
      <div class="cloud-search-page-controls cloud-search-filters-classic cloud-search-filters-mobile-classic ${pageClass}">
        <div class="cloud-search-page-controls__totals">${first} - ${last}&nbsp;of&nbsp;${data.total} results${label}</div>
        ${collectionPage ? sortBlock : ''}
        <div class="cloud-search-page-controls__filters-container">
          <a href="#" class="cloud-search-page-controls__filters-link" data-ba-filters-toggle>
            <span class="cloud-search-filters-link-icon"></span><span class="cloud-search-filters-link-text">Filter</span>
          </a>
          ${collectionPage ? '' : sortBlock}
        </div>
        <div class="cloud-search-page-controls__applied-filters">${appliedFiltersHtml(state, { collectionSearch })}</div>
      </div>`;
  }

  function paginationHtml(data, state, basePath) {
    if (data.pages <= 1) return '';
    const link = (page) => {
      const s = { ...state, page };
      return `${basePath}?${stateToParams(s)}`;
    };
    const caret = '<span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M 1.5 1 L 5 4.5 L 8.5 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>';
    // Shopify-style parts: 1 … c-1 c c+1 … N
    const parts = [];
    for (let p = 1; p <= data.pages; p++) {
      if (p === 1 || p === data.pages || Math.abs(p - data.page) <= 1) parts.push(p);
      else if (parts[parts.length - 1] !== '…') parts.push('…');
    }
    let items = '';
    if (data.page > 1) {
      // Class names mirror snippets/pagination.liquid verbatim (previous uses
      // --next and vice versa there; the CSS depends on it).
      items += `<li><a href="${link(data.page - 1)}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Previous page">${caret}</a></li>`;
    }
    for (const p of parts) {
      if (p === '…') {
        items += '<li><span class="pagination__item">…</span></li>';
      } else if (p === data.page) {
        items += `<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ${p}">${p}</a></li>`;
      } else {
        items += `<li><a href="${link(p)}" class="pagination__item link" aria-label="Page ${p}">${p}</a></li>`;
      }
    }
    if (data.page < data.pages) {
      items += `<li><a href="${link(data.page + 1)}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Next page">${caret}</a></li>`;
    }
    return `
      <div class="pagination-wrapper" data-page="${data.page}">
        <nav class="pagination" role="navigation" aria-label="Pagination">
          <ul class="pagination__list list-unstyled" role="list">${items}</ul>
        </nav>
      </div>`;
  }

  // Degraded card grid used only if the Section Rendering API is unreachable
  // after retries — worker data has image/vendor/price, so it still looks real.
  function fallbackGridHtml(products) {
    const cards = products.map((p) => `
      <li class="grid__item">
        <a href="${esc(p.url)}" class="ba-fallback-card">
          <span class="ba-fallback-card__media">${p.image ? `<img src="${esc(thumb(p.image))}" alt="" loading="lazy">` : ''}</span>
          <span class="ba-fallback-card__price">${money.format(p.price)}${p.compare_at > p.price ? ` <s>${money.format(p.compare_at)}</s>` : ''}</span>
          <span class="ba-fallback-card__vendor">${esc(p.vendor)}</span>
          <span class="ba-fallback-card__title">${esc(p.title)}</span>
        </a>
      </li>`).join('');
    return `<div class="template-search__results collection page-width" id="product-grid">
      <ul class="grid product-grid ba-fallback-grid grid--2-col-tablet-down grid--4-col-desktop" role="list">${cards}</ul></div>`;
  }

  function noResultsHtml(q) {
    // Filters-only dead end (no search term): naming the empty query renders
    // as «for "".» — say what actually happened and offer the actual fix.
    if (!String(q).trim()) {
      return `
        <div class="search-no-results" role="status">
          <h2 class="search-no-results__title">No products found</h2>
          <p class="search-no-results__message">No products match the selected filters.<br>
            Try removing a filter or widening the price range.</p>
          <a href="${esc(window.location.pathname)}" class="search-no-results__button">Clear Filters</a>
        </div>`;
    }
    return `
      <div class="search-no-results" role="status">
        <h2 class="search-no-results__title">No products found</h2>
        <p class="search-no-results__message">We couldn't find anything for "<strong>${esc(q)}</strong>."<br>
          Try a different search term or browse our collections.</p>
        <a href="/pages/categories" class="search-no-results__button">Browse Categories</a>
      </div>`;
  }

  // Fetch server-rendered theme cards for the ranked handles, reorder, return <ul>.
  // Shopify throttles /search under rapid successive requests (filter changes),
  // so transient failures are expected — retry with backoff before giving up.
  async function fetchCards(products) {
    if (!products.length) return null;
    const q = products.map((p) => `handle:${p.handle}`).join(' OR ');
    const url = `/search?q=${encodeURIComponent(q)}&type=product&section_id=ba-search-grid`;
    let ul = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt) await new Promise((r) => setTimeout(r, attempt * 500));
      try {
        const res = await fetch(url);
        if (!res.ok) continue; // throttled/5xx — worth retrying
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        ul = doc.querySelector('[data-ba-search-cards]');
        if (ul) break;
        // 200 without our marker = the serving theme has no ba-search-grid
        // section (e.g. an expired preview session now answered by the live
        // theme). Deterministic — retrying just burns seconds.
        throw new Error('ba-search-grid section not in serving theme');
      } catch (e) {
        if (String(e.message).includes('serving theme')) throw e;
        /* network hiccup — retry */
      }
    }
    if (!ul) throw new Error('section render failed after retries');
    const byHandle = new Map(
      [...ul.querySelectorAll('li[data-ba-handle]')].map((li) => [li.getAttribute('data-ba-handle'), li])
    );
    const ordered = document.createElement('ul');
    ordered.className = ul.className;
    ordered.setAttribute('role', 'list');
    for (const p of products) {
      const li = byHandle.get(p.handle);
      if (li) ordered.appendChild(li);
    }
    // The theme's card-entrance script (inline in card markup) only ever runs
    // for server-rendered cards — innerHTML-injected scripts don't execute, so
    // these cards would stay at the base opacity:0 forever. Replicate its
    // behavior exactly: scroll-triggered IntersectionObserver adding .animate
    // (fadeUpCard) with a 50ms stagger, reset on scroll.
    const cards = [...ordered.querySelectorAll('.product-card-wrapper')];
    let delay = 0;
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        setTimeout(() => el.classList.add('animate'), delay);
        delay += 50;
        io.unobserve(el);
      }
    }, { root: null, rootMargin: '10% 0px', threshold: 0.01 });
    document.addEventListener('scroll', () => { delay = 0; }, { passive: true });
    cards.forEach((c) => io.observe(c));
    return ordered;
  }

  /**
   * XCloud-style batched filtering: checkbox/price changes accumulate in a
   * pending state and surface a floating "Show N products" apply button (the
   * existing .cloud-search-filters-apply CSS + the theme's pin script style and
   * position it). Navigation happens on apply — or immediately for sort changes
   * and Enter in the search/price fields.
   */
  function bindSidebarEvents(roots, state, basePath, opts = {}) {
    // Listeners attach to per-render elements (replaced wholesale on each SPA
    // render), never to persistent containers — re-binding must not accumulate.
    const rootList = (Array.isArray(roots) ? roots : [roots]).filter(Boolean);
    if (!rootList.length) return;
    const root = rootList[0]; // the sidebar mount — value queries target it
    const on = (type, fn) => rootList.forEach((r) => r.addEventListener(type, fn));
    const pending = { ...state, types: [...state.types], vendors: [...state.vendors] };
    let applyBtn = null;
    let countSeq = 0;
    let priceTimer = null;

    const isDirty = () =>
      pending.q !== state.q
      || pending.priceMin !== state.priceMin || pending.priceMax !== state.priceMax
      || pending.types.join('||') !== state.types.join('||')
      || pending.vendors.join('||') !== state.vendors.join('||');

    // Filter/sort/chip changes: desktop keeps the visitor's scroll position
    // through the in-place re-render; mobile still returns to the top (the
    // slide-in pane covered the viewport anyway). Pagination and fresh
    // searches keep the default scroll-to-top.
    const filterNavigate = (s) => navigate(s, basePath, {
      scroll: window.matchMedia('(max-width: 749px)').matches,
    });

    const applyPending = () => filterNavigate({ ...pending, page: 1 });

    async function refreshApply() {
      if (!isDirty()) {
        applyBtn?.remove();
        applyBtn = null;
        return;
      }
      if (!applyBtn) {
        applyBtn = document.createElement('button');
        applyBtn.type = 'button';
        applyBtn.className = 'cloud-search-filters-apply';
        applyBtn.textContent = 'Show products';
        applyBtn.addEventListener('click', applyPending);
        (root.querySelector('.cloud-search-filters-sidebar') || root).appendChild(applyBtn);
      }
      const seq = ++countSeq;
      try {
        const params = stateToParams({ ...pending, page: 1 }, { forApi: true });
        params.set('per_page', '1');
        const data = await fetchJSON(`${WORKER}/api/search?${params}`);
        if (seq === countSeq && applyBtn) {
          applyBtn.textContent = `Show ${data.total} product${data.total === 1 ? '' : 's'}`;
        }
      } catch { /* keep the generic label */ }
    }

    function readPendingPrices() {
      pending.priceMin = root.querySelector('[data-ba-price="min"]')?.value.replace(/[^0-9.]/g, '') ?? '';
      pending.priceMax = root.querySelector('[data-ba-price="max"]')?.value.replace(/[^0-9.]/g, '') ?? '';
    }

    // ── Dual-handle price slider (XCloud markup/classes, our drag logic) ──
    const slider = root.querySelector('[data-ba-price-slider]');
    const sliderState = slider ? {
      lo: Number(slider.dataset.min) || 0,
      hi: Number(slider.dataset.max) || 0,
      bar: slider.querySelector('.cloud-search-range-slider__bar'),
      hMin: slider.querySelector('[data-ba-handle="min"]'),
      hMax: slider.querySelector('[data-ba-handle="max"]'),
    } : null;

    function sliderValues() {
      if (!sliderState) return null;
      const { lo, hi } = sliderState;
      const min = pending.priceMin !== '' ? Math.max(lo, Math.min(hi, Number(pending.priceMin))) : lo;
      const max = pending.priceMax !== '' ? Math.max(lo, Math.min(hi, Number(pending.priceMax))) : hi;
      return { min: Math.min(min, max), max: Math.max(min, max) };
    }

    function paintSlider() {
      if (!sliderState || sliderState.hi <= sliderState.lo) return;
      const w = slider.clientWidth;
      if (!w) return;
      const { lo, hi } = sliderState;
      const v = sliderValues();
      const px = (val) => ((val - lo) / (hi - lo)) * w;
      const a = px(v.min);
      const b = px(v.max);
      sliderState.hMin.style.transform = `translateX(${a}px) translate(-50%, 50%)`;
      sliderState.hMax.style.transform = `translateX(${b}px) translate(-50%, 50%)`;
      sliderState.bar.style.transform = `translateX(${a}px) scaleX(${Math.max(0, (b - a) / w)})`;
    }

    function attachSliderDrag() {
      if (!sliderState) return;
      for (const handle of [sliderState.hMin, sliderState.hMax]) {
        handle.addEventListener('pointerdown', (e) => {
          e.preventDefault();
          const which = handle.getAttribute('data-ba-handle');
          handle.classList.add('cloud-search-range-slider__handle--dragging');
          handle.setPointerCapture(e.pointerId);
          const rect = slider.getBoundingClientRect();
          const { lo, hi } = sliderState;
          const onMove = (ev) => {
            const frac = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
            let val = Math.round(lo + frac * (hi - lo));
            const v = sliderValues();
            if (which === 'min') val = Math.min(val, v.max);
            else val = Math.max(val, v.min);
            if (which === 'min') {
              pending.priceMin = val <= lo ? '' : String(val);
              const inp = root.querySelector('[data-ba-price="min"]');
              if (inp) inp.value = pending.priceMin;
            } else {
              pending.priceMax = val >= hi ? '' : String(val);
              const inp = root.querySelector('[data-ba-price="max"]');
              if (inp) inp.value = pending.priceMax;
            }
            paintSlider();
          };
          const onUp = (ev) => {
            handle.classList.remove('cloud-search-range-slider__handle--dragging');
            handle.releasePointerCapture(ev.pointerId);
            handle.removeEventListener('pointermove', onMove);
            handle.removeEventListener('pointerup', onUp);
            refreshApply();
          };
          handle.addEventListener('pointermove', onMove);
          handle.addEventListener('pointerup', onUp);
        });
      }
      paintSlider();
    }
    attachSliderDrag();

    on('change', (e) => {
      const t = e.target;
      if (t.matches('[data-ba-facet]')) {
        const key = t.getAttribute('data-ba-facet');
        const list = new Set(pending[key]);
        t.checked ? list.add(t.value) : list.delete(t.value);
        pending[key] = [...list];
        refreshApply();
      }
    });
    on('keydown', (e) => {
      const t = e.target;
      if (e.key !== 'Enter') return;
      if (t.matches('[data-ba-price]')) {
        readPendingPrices();
        applyPending();
      } else if (t.matches('[data-ba-collection-search]')) {
        pending.q = t.value.trim();
        // Enter commits the query, so the suggestion menu has served its
        // purpose - it used to stay open over the results.
        hideMenu();
        t.blur();
        applyPending();
      }
    });
    on('input', (e) => {
      const t = e.target;
      if (t.matches('[data-ba-price]')) {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(() => { readPendingPrices(); paintSlider(); refreshApply(); }, 350);
      } else if (t.matches('[data-ba-collection-search]')) {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(() => { pending.q = t.value.trim(); refreshApply(); }, 350);
      } else if (t.matches('[data-ba-value-search]')) {
        const needle = t.value.trim().toLowerCase();
        const values = t.closest('.cloud-search-filter').querySelectorAll('.cloud-search-filter-value');
        for (const label of values) {
          const name = label.getAttribute('data-filter-value') || '';
          label.style.display = !needle || name.toLowerCase().includes(needle) ? '' : 'none';
        }
      }
    });
    on('click', (e) => {
      const removeBtn = e.target.closest('[data-ba-remove-filter]');
      if (removeBtn) {
        const key = removeBtn.getAttribute('data-ba-remove-filter');
        const s = { ...state, page: 1 };
        if (key === 'types') s.types = [];
        else if (key === 'vendors') s.vendors = [];
        else if (key === 'price') { s.priceMin = ''; s.priceMax = ''; }
        else if (key === 'q') s.q = '';
        filterNavigate(s);
        return;
      }
      if (e.target.closest('[data-ba-clear-filters]')) {
        const s = { ...state, types: [], vendors: [], priceMin: '', priceMax: '', page: 1 };
        if (state.collection) s.q = ''; // the in-collection search acts as a filter
        filterNavigate(s);
        return;
      }
      const sortBtn = e.target.closest('[data-ba-sort-btn]');
      if (sortBtn) {
        const list = sortBtn.closest('[data-ba-sort-dd]').querySelector('[data-ba-sort-list]');
        const willOpen = list.hidden;
        list.hidden = !willOpen;
        sortBtn.setAttribute('aria-expanded', String(willOpen));
        return;
      }
      const sortOpt = e.target.closest('[data-ba-sort-option]');
      if (sortOpt) {
        filterNavigate({ ...state, sort: sortOpt.getAttribute('data-ba-sort-option'), page: 1 });
        return;
      }
      const toggle = e.target.closest('[data-ba-filters-toggle]');
      if (toggle) {
        e.preventDefault();
        openFilterPane(opts.data, state, basePath, opts);
      }
    });
  }

  // Close any open sort dropdown on outside click / Escape. Bound once at the
  // document level so it survives every controls re-render.
  document.addEventListener('click', (e) => {
    for (const dd of document.querySelectorAll('[data-ba-sort-dd]')) {
      if (dd.contains(e.target)) continue;
      const list = dd.querySelector('[data-ba-sort-list]');
      if (list && !list.hidden) {
        list.hidden = true;
        dd.querySelector('[data-ba-sort-btn]')?.setAttribute('aria-expanded', 'false');
      }
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    for (const dd of document.querySelectorAll('[data-ba-sort-dd]')) {
      const list = dd.querySelector('[data-ba-sort-list]');
      if (list && !list.hidden) {
        list.hidden = true;
        dd.querySelector('[data-ba-sort-btn]')?.setAttribute('aria-expanded', 'false');
      }
    }
  });

  // ────────────────────────────────────────────────────────────────
  // Results page (/search)
  // ────────────────────────────────────────────────────────────────

  // Superseding renders cancel stragglers: any await checks the sequence after.
  let renderSeq = 0;

  // Next-page prefetch: while the visitor reads page N, page N+1's worker data
  // AND its Shopify-rendered cards are fetched in the background — clicking
  // "next" swaps instantly from memory instead of waiting on the Section
  // Rendering API.
  let prefetched = null; // { key, data, ul }

  function stateKey(state) {
    return stateToParams(state, { forApi: true }).toString();
  }

  function prefetchNextPage(state, data) {
    if (data.page >= data.pages) return;
    const nextState = { ...state, page: data.page + 1 };
    const key = stateKey(nextState);
    if (prefetched?.key === key) return;
    (async () => {
      try {
        const nd = await fetchJSON(`${WORKER}/api/search?${stateToParams(nextState, { forApi: true })}`);
        const ul = nd.products.length ? await fetchCards(nd.products) : null;
        prefetched = { key, data: nd, ul };
      } catch { /* best effort — the click path just fetches normally */ }
    })();
  }

  async function renderResultsPage(state, { scroll = false } = {}) {
    const sidebar = document.getElementById('cloud_search_filters_sidebar');
    const controlsMount = document.getElementById('cloud_search_filters_root');
    const gridContainer = document.getElementById('BaSearchGridContainer');
    if (!controlsMount || !gridContainer) return;
    if (!state.q) return;
    const basePath = '/search';
    const seq = ++renderSeq;
    if (scroll) window.scrollTo({ top: 0 });

    // Prefetch hit (typically: "next page") — render synchronously, zero wait.
    if (prefetched?.key === stateKey(state) && prefetched.data) {
      const { data, ul } = prefetched;
      prefetched = null;
      controlsMount.innerHTML = controlsHtml(data, state);
      if (sidebar) sidebar.innerHTML = sidebarHtml(data, state);
      bindSidebarEvents([sidebar?.firstElementChild, controlsMount.firstElementChild], state, basePath, { data });
      const wrap = document.createElement('div');
      wrap.className = 'template-search__results collection page-width';
      wrap.id = 'product-grid';
      if (ul) wrap.appendChild(ul);
      wrap.insertAdjacentHTML('beforeend', paginationHtml(data, state, basePath));
      gridContainer.innerHTML = '';
      gridContainer.appendChild(wrap);
      prefetchNextPage(state, data);
      return;
    }

    // Stale results leave the screen the moment a new query is requested—
    // old products must never masquerade as the new answer. Height is held so
    // the layout doesn't jump while the new cards load. (Prefetched pages
    // above swap with no gap at all.) The stub child matters: base.css sets
    // div:empty{display:none}, which would collapse an emptied container.
    gridContainer.style.minHeight = `${gridContainer.offsetHeight}px`;
    gridContainer.innerHTML = '<div></div>';
    const settle = () => { gridContainer.style.minHeight = ''; };

    let data;
    try {
      data = await fetchJSON(`${WORKER}/api/search?${stateToParams(state, { forApi: true })}`);
    } catch (e) {
      if (seq !== renderSeq) return;
      console.error('ba-search results failed:', e);
      gridContainer.innerHTML = noResultsHtml(state.q);
      settle();
      return;
    }
    if (seq !== renderSeq) return;

    // Prod parity: a plain no-results page renders no controls row and no
    // sidebar — just the message. With filters engaged we keep them so the
    // visitor can back out of the filter that zeroed the results.
    const filtersEngaged = !!(state.types.length || state.vendors.length
      || state.priceMin !== '' || state.priceMax !== '');
    if (!data.products.length && !filtersEngaged) {
      controlsMount.innerHTML = '';
      if (sidebar) sidebar.innerHTML = '';
      gridContainer.innerHTML = noResultsHtml(state.q);
      settle();
      return;
    }

    controlsMount.innerHTML = controlsHtml(data, state);
    if (sidebar) sidebar.innerHTML = sidebarHtml(data, state);
    bindSidebarEvents([sidebar?.firstElementChild, controlsMount.firstElementChild], state, basePath, { data });

    if (!data.products.length) {
      gridContainer.innerHTML = noResultsHtml(state.q);
      settle();
      return;
    }
    try {
      const ul = await fetchCards(data.products);
      if (seq !== renderSeq) return;
      const wrap = document.createElement('div');
      wrap.className = 'template-search__results collection page-width';
      wrap.id = 'product-grid';
      if (ul) wrap.appendChild(ul);
      wrap.insertAdjacentHTML('beforeend', paginationHtml(data, state, basePath));
      gridContainer.innerHTML = '';
      gridContainer.appendChild(wrap);
    } catch (e) {
      if (seq !== renderSeq) return;
      console.error('ba-search cards failed:', e);
      // Degraded mode: build presentable cards from the worker's own data.
      gridContainer.innerHTML = fallbackGridHtml(data.products) + paginationHtml(data, state, basePath);
    }
    settle();
    prefetchNextPage(state, data);
  }

  // ────────────────────────────────────────────────────────────────
  // In-collection filters + search
  // ────────────────────────────────────────────────────────────────

  // True once we replace the native grid — un-engaging after that needs a full
  // reload to get Shopify's server-rendered grid back.
  let collectionGridTakenOver = false;

  // One toolbar for collection pages at every width: Filter and Sort on the
  // left, the product count on the right. XCloud's row was mobile-only and
  // lived inside Dawn's mobile-facets wrapper, so it could never serve desktop.
  const FILTER_ICON = `<svg class="ba-coll-bar__icon" viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" d="M1 4h3.185A2.995 2.995 0 0 0 7 6a2.995 2.995 0 0 0 2.815-2H19a1 1 0 1 0 0-2H9.815A2.995 2.995 0 0 0 7 0a2.995 2.995 0 0 0-2.815 2H1a1 1 0 1 0 0 2m18 12H9.815A2.995 2.995 0 0 0 7 14a2.995 2.995 0 0 0-2.815 2H1a1 1 0 1 0 0 2h3.185A2.995 2.995 0 0 0 7 20a2.995 2.995 0 0 0 2.815-2H19a1 1 0 1 0 0-2m0-7h-3.185A2.995 2.995 0 0 0 13 7a2.995 2.995 0 0 0-2.815 2H1a1 1 0 1 0 0 2h9.185A2.995 2.995 0 0 0 13 13a2.995 2.995 0 0 0 2.815-2H19a1 1 0 1 0 0-2M7 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2m0 16a1 1 0 1 1 0-2 1 1 0 0 1 0 2m6-7a1 1 0 1 1 0-2 1 1 0 0 1 0 2"/></svg>`;

  const SORT_ICON = `<svg class="ba-coll-bar__icon" viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 16V4M6 4 3 7m3-3 3 3M14 4v12m0 0 3-3m-3 3-3-3"/></svg>`;

  function collectionBarHtml(data, state) {
    return `
      <div class="ba-coll-bar">
        <a href="#" class="ba-coll-bar__filter" data-ba-filters-toggle>${FILTER_ICON}<span>Filter</span></a>
        <div class="ba-coll-bar__sort">
          ${SORT_ICON}
          <span class="ba-coll-bar__sort-label">Sort by:</span>
          ${sortDropdownHtml(state)}
        </div>
        <div class="ba-coll-bar__count">${data.total} products</div>
        <div class="ba-coll-bar__applied">${appliedFiltersHtml(state, { collectionSearch: true })}</div>
      </div>`;
  }

  function ensureCollectionBarRoot() {
    let root = document.getElementById('ba_collection_bar');
    if (!root) {
      const grid = document.getElementById('ProductGridContainer');
      if (!grid) return null;
      root = document.createElement('div');
      root.id = 'ba_collection_bar';
      grid.parentElement.insertBefore(root, grid);
    }
    return root;
  }

  async function renderCollectionPage(state, { scroll = false } = {}) {
    const sidebar = document.getElementById('cloud_search_filters_sidebar');
    if (!sidebar || !cfg.collection) return;
    // Callers that build state from query params alone (pagination clicks)
    // don't know about the path-carried tag — the URL is already updated by
    // the time we render, so recover it here.
    if (state.tag === undefined) state.tag = collectionTagFromPath();

    const basePath = window.location.pathname;
    const engaged = !!(state.q || state.priceMin || state.priceMax || state.types.length || state.vendors.length
      || (state.sort && state.sort !== 'relevance'));
    const seq = ++renderSeq;
    if (scroll) window.scrollTo({ top: 0 });

    if (!engaged && collectionGridTakenOver) {
      window.location.reload();
      return;
    }

    let data;
    try {
      data = await fetchJSON(`${WORKER}/api/search?${stateToParams(state, { forApi: true })}`);
    } catch (e) {
      console.error('ba-search collection failed:', e);
      // Never leave the native grid hidden if the worker is unreachable.
      document.documentElement.classList.add('ba-search-ready');
      return;
    }
    if (seq !== renderSeq) return;

    sidebar.innerHTML = sidebarHtml(data, state, { collectionSearch: true });

    // Untouched visit → keep the native server-rendered grid (preserves manual
    // collection sort). Take the grid over only when the visitor engages.
    if (!engaged) {
      const bar = ensureCollectionBarRoot();
      if (bar) bar.innerHTML = collectionBarHtml(data, state);
      bindSidebarEvents(
        [sidebar.firstElementChild, bar?.firstElementChild],
        state, basePath, { data, collectionSearch: true }
      );
      // In case the head script hid the grid on a URL we ended up not engaging.
      document.documentElement.classList.add('ba-search-ready');
      return;
    }

    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) {
      bindSidebarEvents([sidebar.firstElementChild], state, basePath, { data, collectionSearch: true });
      return;
    }

    // The toolbar sits above the grid in both states, so it stays put when the
    // visitor engages. Clear XCloud's legacy row in case it rendered earlier.
    document.getElementById('cloud_search_filters_root')?.replaceChildren();
    const bar = ensureCollectionBarRoot();
    if (bar) bar.innerHTML = collectionBarHtml(data, state);

    // Our controls row replaces the native toolbar — hide the theme's sort form,
    // facet aside and "N products" count so totals aren't shown twice.
    for (const el of document.querySelectorAll(
      'facet-filters-form, #main-collection-filters, .collection .product-count'
    )) {
      el.style.display = 'none';
    }
    // Stale products leave the screen immediately; height held to avoid jump.
    // (Stub child: base.css div:empty{display:none} would collapse it.)
    gridContainer.style.minHeight = `${gridContainer.offsetHeight}px`;
    gridContainer.innerHTML = '<div></div>';
    let wrap = null;
    try {
      const ul = await fetchCards(data.products);
      if (seq !== renderSeq) return;
      wrap = document.createElement('div');
      wrap.className = 'collection page-width';
      wrap.id = 'product-grid';
      if (data.products.length && ul) wrap.appendChild(ul);
      else wrap.insertAdjacentHTML('beforeend', noResultsHtml(state.q || ''));
      wrap.insertAdjacentHTML('beforeend', paginationHtml(data, state, basePath));
      gridContainer.innerHTML = '';
      gridContainer.appendChild(wrap);
    } catch (e) {
      if (seq !== renderSeq) return;
      console.error('ba-search collection grid failed:', e);
      // Degraded mode — filtered results from worker data beat showing the
      // unfiltered native grid under filtered controls.
      wrap = document.createElement('div');
      wrap.innerHTML = (data.products.length ? fallbackGridHtml(data.products) : noResultsHtml(state.q || ''))
        + paginationHtml(data, state, basePath);
      gridContainer.innerHTML = '';
      gridContainer.appendChild(wrap);
    } finally {
      gridContainer.style.minHeight = '';
      collectionGridTakenOver = true;
      // Reveal the grid area (the head script hid it to prevent the
      // all-products flash on filtered URLs).
      document.documentElement.classList.add('ba-search-ready');
    }
    bindSidebarEvents(
      [sidebar.firstElementChild, bar?.firstElementChild, wrap],
      state, basePath, { data, collectionSearch: true }
    );
  }

  // ────────────────────────────────────────────────────────────────

  function init() {
    attachPresearch();
    if (cfg.template === 'search') {
      spaRender = renderResultsPage;
      renderResultsPage(readState());
    } else if (cfg.template === 'collection') {
      spaRender = (s, o) => renderCollectionPage({ ...s, collection: cfg.collection }, o);
      renderCollectionPage({ ...readState(), collection: cfg.collection });
    }
    if (!spaRender) return;

    // Public hook for collection-tabs.liquid: re-render sidebar/grid from the
    // current URL after a tab switch (pushState happens outside this file).
    window.BASearch = {
      render: (o) => spaRender(readState(), o || { scroll: false }),
    };

    window.addEventListener('popstate', () => {
      const s = readState();
      if (cfg.template === 'search') syncSearchInputs(s.q || '');
      spaRender(s, { scroll: false });
    });

    // Pagination renders in place too — the hrefs stay for middle-click/SEO.
    document.addEventListener('click', (e) => {
      const a = e.target.closest?.(
        '#BaSearchGridContainer a.pagination__item[href], #ProductGridContainer a.pagination__item[href]'
      );
      if (!a) return;
      // Non-engaged collection pages keep Shopify's native grid — its
      // pagination must navigate natively. Intercepting it just rewrote the
      // URL and scrolled to top while page 1 stayed on screen.
      if (a.closest('#ProductGridContainer') && !collectionGridTakenOver) return;
      e.preventDefault();
      const url = new URL(a.href, window.location.origin);
      navigate(readStateFrom(url.searchParams), url.pathname);
    });
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
})();
